export type RegionCode =
  | 'warminsko-mazurskie'
  | 'mazowieckie'
  | 'pomorskie'
  | 'podlaskie'
  | 'lubelskie'
  | 'podkarpackie'
  | 'malopolskie'
  | 'slaskie'
  | 'dolnoslaskie'
  | 'wielkopolskie'
  | 'lodzkie'
  | 'kujawsko-pomorskie'
  | 'zachodniopomorskie'
  | 'lubuskie'
  | 'opolskie'
  | 'swietokrzyskie'
  | 'default';

export interface RegionalMaterialBenchmark {
  materialId: string;
  region: RegionCode;
  unit: 'l' | 'kg' | 'szt' | 'mb' | 'm2' | 'zestaw';
  minNet: number;
  avgNet: number;
  maxNet: number;
  sourceLabel: string;
  updatedAt: string;
}

export type MaterialPriceStatus = 'below_market' | 'within_market' | 'above_market' | 'unknown';

export interface MaterialPriceComparison {
  status: MaterialPriceStatus;
  message: string;
  benchmark?: RegionalMaterialBenchmark;
}

const UPDATED_AT = '2026-05-21';
const SOURCE_LABEL = 'Benchmark lokalny ProWycena MVP';

const REGION_MULTIPLIERS: Record<RegionCode, number> = {
  default: 1,
  'warminsko-mazurskie': 0.92,
  mazowieckie: 1.15,
  pomorskie: 1.1,
  podlaskie: 0.94,
  lubelskie: 0.95,
  podkarpackie: 0.94,
  malopolskie: 1.08,
  slaskie: 1.05,
  dolnoslaskie: 1.06,
  wielkopolskie: 1.03,
  lodzkie: 0.99,
  'kujawsko-pomorskie': 0.97,
  zachodniopomorskie: 1.04,
  lubuskie: 0.98,
  opolskie: 0.97,
  swietokrzyskie: 0.94,
};

const DEFAULT_BENCHMARKS: Record<string, Omit<RegionalMaterialBenchmark, 'region' | 'sourceLabel' | 'updatedAt'>> = {
  paint_l: { materialId: 'paint_l', unit: 'l', minNet: 24, avgNet: 32, maxNet: 48 },
  primer_l: { materialId: 'primer_l', unit: 'l', minNet: 8, avgNet: 12, maxNet: 18 },
  plaster_kg: { materialId: 'plaster_kg', unit: 'kg', minNet: 1.5, avgNet: 2.2, maxNet: 3.5 },
  sandpaper_szt: { materialId: 'sandpaper_szt', unit: 'szt', minNet: 2, avgNet: 4, maxNet: 7 },
  painting_foil_m2: { materialId: 'painting_foil_m2', unit: 'm2', minNet: 0.8, avgNet: 1.4, maxNet: 2.4 },
  masking_tape_mb: { materialId: 'masking_tape_mb', unit: 'mb', minNet: 0.25, avgNet: 0.45, maxNet: 0.8 },
};

export function normalizeRegionCode(region?: string): RegionCode {
  const normalized = (region ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/_/g, '-')
    .trim();

  if (isRegionCode(normalized)) return normalized;
  return 'default';
}

export function getRegionalMaterialBenchmark(
  materialId: string,
  regionInput?: string
): RegionalMaterialBenchmark | undefined {
  const seed = DEFAULT_BENCHMARKS[materialId];
  if (!seed) return undefined;

  const region = normalizeRegionCode(regionInput);
  const multiplier = REGION_MULTIPLIERS[region] ?? REGION_MULTIPLIERS.default;

  return {
    ...seed,
    region,
    minNet: roundPrice(seed.minNet * multiplier),
    avgNet: roundPrice(seed.avgNet * multiplier),
    maxNet: roundPrice(seed.maxNet * multiplier),
    sourceLabel: SOURCE_LABEL,
    updatedAt: UPDATED_AT,
  };
}

export function compareMaterialPriceToRegion(input: {
  materialId: string;
  region?: string;
  userUnitPriceNet?: number;
}): MaterialPriceComparison {
  const benchmark = getRegionalMaterialBenchmark(input.materialId, input.region);

  if (!benchmark || !input.userUnitPriceNet || input.userUnitPriceNet <= 0) {
    return {
      status: 'unknown',
      message: 'Brak benchmarku regionalnego dla tej pozycji materiałowej.',
      benchmark,
    };
  }

  if (input.userUnitPriceNet < benchmark.minNet) {
    return {
      status: 'below_market',
      message: `Cena materiału jest poniżej typowych widełek dla woj. ${formatRegionName(benchmark.region)}. Sprawdź, czy uwzględniono standard, kolor i zapas.`,
      benchmark,
    };
  }

  if (input.userUnitPriceNet > benchmark.maxNet) {
    return {
      status: 'above_market',
      message: `Cena materiału jest powyżej typowych widełek dla woj. ${formatRegionName(benchmark.region)}. To może być poprawne przy wyższym standardzie.`,
      benchmark,
    };
  }

  return {
    status: 'within_market',
    message: `Cena materiału mieści się w typowych widełkach dla woj. ${formatRegionName(benchmark.region)}.`,
    benchmark,
  };
}

function isRegionCode(value: string): value is RegionCode {
  return value in REGION_MULTIPLIERS;
}

function roundPrice(value: number): number {
  return Math.round(value * 100) / 100;
}

function formatRegionName(region: RegionCode): string {
  if (region === 'default') return 'domyślnego regionu';
  const labels: Record<RegionCode, string> = {
    default: 'domyślnego regionu',
    'warminsko-mazurskie': 'warmińsko-mazurskiego',
    mazowieckie: 'mazowieckiego',
    pomorskie: 'pomorskiego',
    podlaskie: 'podlaskiego',
    lubelskie: 'lubelskiego',
    podkarpackie: 'podkarpackiego',
    malopolskie: 'małopolskiego',
    slaskie: 'śląskiego',
    dolnoslaskie: 'dolnośląskiego',
    wielkopolskie: 'wielkopolskiego',
    lodzkie: 'łódzkiego',
    'kujawsko-pomorskie': 'kujawsko-pomorskiego',
    zachodniopomorskie: 'zachodniopomorskiego',
    lubuskie: 'lubuskiego',
    opolskie: 'opolskiego',
    swietokrzyskie: 'świętokrzyskiego',
  };

  return labels[region];
}
